import { supabase } from '../lib/supabase';

export const rewardsService = {
  // Get user points balance and tier info
  async getUserPoints(userId) {
    try {
      const { data, error } = await supabase?.from('user_points')?.select('*')?.eq('user_id', userId)?.single();

      if (error) throw error;
      
      return {
        totalPoints: data?.total_points || 0,
        lifetimeEarned: data?.lifetime_earned || 0,
        lifetimeRedeemed: data?.lifetime_redeemed || 0,
        tier: data?.tier || 'Bronze',
        streakDays: data?.streak_days || 0,
        lastActivityDate: data?.last_activity_date
      };
    } catch (error) {
      console.error('Error fetching user points:', error);
      throw error;
    }
  },

  // Get point transaction history
  async getPointTransactions(userId, limit = 50) {
    try {
      const { data, error } = await supabase?.from('point_transactions')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      
      return data?.map(tx => ({
        id: tx?.id,
        transactionType: tx?.transaction_type,
        points: tx?.points,
        category: tx?.category,
        description: tx?.description,
        referenceId: tx?.reference_id,
        referenceTable: tx?.reference_table,
        createdAt: tx?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  },

  // Get available redemption options
  async getRedemptionOptions() {
    try {
      const { data, error } = await supabase?.from('redemption_options')?.select('*')?.eq('is_active', true)?.order('display_order', { ascending: true });

      if (error) throw error;
      
      return data?.map(option => ({
        id: option?.id,
        title: option?.title,
        description: option?.description,
        pointCost: option?.point_cost,
        benefitType: option?.benefit_type,
        benefitValue: option?.benefit_value,
        requiresPro: option?.requires_pro,
        displayOrder: option?.display_order
      })) || [];
    } catch (error) {
      console.error('Error fetching redemption options:', error);
      throw error;
    }
  },

  // Get user redemption history
  async getUserRedemptions(userId) {
    try {
      const { data, error } = await supabase?.from('user_redemptions')?.select(`
          *,
          redemption_options (
            title,
            description,
            benefit_type
          )
        `)?.eq('user_id', userId)?.order('redeemed_at', { ascending: false });

      if (error) throw error;
      
      return data?.map(redemption => ({
        id: redemption?.id,
        pointsSpent: redemption?.points_spent,
        status: redemption?.status,
        redeemedAt: redemption?.redeemed_at,
        completedAt: redemption?.completed_at,
        title: redemption?.redemption_options?.title,
        description: redemption?.redemption_options?.description,
        benefitType: redemption?.redemption_options?.benefit_type
      })) || [];
    } catch (error) {
      console.error('Error fetching user redemptions:', error);
      throw error;
    }
  },

  // Redeem points for a reward
  async redeemReward(userId, redemptionOptionId) {
    try {
      const { data, error } = await supabase?.rpc('redeem_points', {
        p_user_id: userId,
        p_redemption_option_id: redemptionOptionId
      });

      if (error) throw error;
      return { redemptionId: data };
    } catch (error) {
      console.error('Error redeeming reward:', error);
      throw error;
    }
  },

  // Award points (called by other services when user completes actions)
  async awardPoints(userId, points, category, description, referenceId = null, referenceTable = null) {
    try {
      const { error } = await supabase?.rpc('award_points', {
        p_user_id: userId,
        p_points: points,
        p_category: category,
        p_description: description,
        p_reference_id: referenceId,
        p_reference_table: referenceTable
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error awarding points:', error);
      throw error;
    }
  },

  // Get tier progress info
  getTierProgress(lifetimeEarned) {
    const tiers = [
      { name: 'Bronze', threshold: 0, next: 200000 },
      { name: 'Silver', threshold: 200000, next: 500000 },
      { name: 'Gold', threshold: 500000, next: 1000000 },
      { name: 'Platinum', threshold: 1000000, next: null }
    ];

    const currentTier = tiers?.find((tier, index) => {
      const nextTier = tiers?.[index + 1];
      return lifetimeEarned >= tier?.threshold && (!nextTier || lifetimeEarned < nextTier?.threshold);
    }) || tiers?.[0];

    const progress = currentTier?.next 
      ? ((lifetimeEarned - currentTier?.threshold) / (currentTier?.next - currentTier?.threshold)) * 100
      : 100;

    return {
      currentTier: currentTier?.name,
      nextTier: currentTier?.next ? tiers?.[tiers?.indexOf(currentTier) + 1]?.name : null,
      progress: Math.min(progress, 100),
      pointsToNext: currentTier?.next ? currentTier?.next - lifetimeEarned : 0
    };
  },

  // Get earning opportunities (static list)
  getEarningOpportunities() {
    return [
      {
        id: 'account_creation',
        title: 'Account Creation',
        points: 50000,
        category: 'account_setup',
        description: 'Sign up and create your account',
        icon: 'UserPlus',
        completed: true // Always completed if user exists
      },
      {
        id: 'onboarding',
        title: 'Complete Onboarding',
        points: 10000,
        category: 'account_setup',
        description: 'Finish the onboarding checklist',
        icon: 'CheckCircle'
      },
      {
        id: 'first_account',
        title: 'Add First Account',
        points: 5000,
        category: 'account_setup',
        description: 'Connect your first financial account',
        icon: 'Wallet'
      },
      {
        id: 'all_accounts',
        title: 'Add All Account Types',
        points: 10000,
        category: 'account_setup',
        description: 'Add accounts from all required types',
        icon: 'Layers'
      },
      {
        id: 'create_budget',
        title: 'Create a Budget',
        points: 5000,
        category: 'financial_habits',
        description: 'Set up your first budget',
        icon: 'Target'
      },
      {
        id: 'budget_adherence',
        title: 'Stay Within Budget',
        points: 15000,
        category: 'financial_habits',
        description: 'Stay within budget for a full month',
        icon: 'TrendingUp'
      },
      {
        id: 'complete_goal',
        title: 'Complete Financial Goal',
        points: 20000,
        category: 'financial_habits',
        description: 'Reach a financial goal target',
        icon: 'Award'
      },
      {
        id: 'recurring_transaction',
        title: 'Add Recurring Transaction',
        points: 5000,
        category: 'financial_habits',
        description: 'Set up a recurring transaction',
        icon: 'Repeat'
      },
      {
        id: 'reconcile_account',
        title: 'Reconcile Account',
        points: 5000,
        category: 'financial_habits',
        description: 'Reconcile an account balance',
        icon: 'CheckSquare'
      },
      {
        id: 'weekly_checkin',
        title: 'Weekly Check-in',
        points: 1000,
        category: 'engagement',
        description: 'Log in and check your finances weekly',
        icon: 'Calendar'
      },
      {
        id: 'monthly_streak',
        title: 'Monthly Activity Streak',
        points: 10000,
        category: 'engagement',
        description: 'Maintain activity for a full month',
        icon: 'Flame'
      },
      {
        id: 'view_insights',
        title: 'View Insights',
        points: 2000,
        category: 'engagement',
        description: 'Check your financial insights or reports',
        icon: 'BarChart3'
      },
      {
        id: 'upgrade_pro',
        title: 'Upgrade to Pro',
        points: 50000,
        category: 'subscription',
        description: 'Upgrade to Pro subscription',
        icon: 'Crown'
      },
      {
        id: 'annual_pro',
        title: 'Annual Pro Subscription',
        points: 25000,
        category: 'subscription',
        description: 'Subscribe to annual Pro plan',
        icon: 'Star'
      },
      {
        id: 'referral',
        title: 'Refer a Friend',
        points: 20000,
        category: 'referral',
        description: 'Invite friends and earn up to 350 points per successful referral',
        icon: 'Users'
      }
    ];
  }
};